import { ApiError, type IntakeQuestionPayload, type IntakeReviewPayload, type IntakeTurn, type RequisitionReviewPayload, type ResumeValue, type RunView } from "@procurement-agents/scope-api";
import { HandsFree, MicSession, NotHeard, SentenceChunker, useSpeechPlayer, type Capture, type HandsFreePorts, type HandsFreeReply, type HandsFreeState, type Pending, type Phrase, type Turn } from "@procurement-agents/voice";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { useOptionalScopeApi } from "./api";
import { warmSpeech } from "./voice";

/**
 * Voice in the intake (CHANGE_voice_level3 §14): one control, **Voice**, which is the hands-free conversation. On,
 * the agent speaks each question (sentence by sentence as it streams, the option list last), listens, sends the
 * answer when the person stops talking, reads back the answers the tenant wants confirmed, and hears control words.
 * The chat and the composer stay; typing takes the turn over. Off, it is a typed chat. The mic on the composer is
 * separate: dictation, always there where the workspace has voice.
 *
 * One session for the whole scope agent, so it carries on from New request into the request it creates. The state
 * machine is `HandsFree` in packages/voice; this provides its ports and keeps the screen awake.
 */
export interface HandsFreeSettings { available: boolean; phrases: Record<string, string> | null }

/** The microphone as the session uses it. `MicSession` in the browser; a fake in tests. */
export interface MicLike {
  readonly device: string | null;
  readonly audioContext?: AudioContext;
  capture(opts: { timeoutMs: number; vad?: { maxMs: number } }): Pending<Capture>;
  watch(): Pending<boolean>;
  close(): void;
}

interface HandsFreeApi {
  available: boolean;
  /** The Voice toggle, remembered per device. On does not by itself open the microphone: that needs a tap. */
  on: boolean;
  /** The session is running (the microphone is open). */
  active: boolean;
  state: HandsFreeState | null;
  phrases: Record<string, string>;
  bargeIn: boolean;
  setBargeIn(on: boolean): void;
  problem: string | null;
  /** From a tap: switch Voice on or off. On starts the session; off stops it and the recorder. */
  toggle(on: boolean): Promise<void>;
  /** Voice on without starting: the shortcut on a browser that needs a tap for sound ("Tap to begin"). */
  prefer(on: boolean): void;
  /** From a tap: start the session with Voice already on ("Tap to resume", "Tap to begin"). */
  begin(): Promise<boolean>;
  /** Stop listening and speaking now; Voice stays on, and a tap resumes. */
  stop(): void;
  /** The person typed: this turn is theirs. */
  yieldTurn(): void;
  /** Say one sentence of a question that is still streaming in. */
  speakChunk(text: string): void;
  /** The question text spoken so far by `speakChunk`, to tell a turn it has been heard already. */
  streamedText(): string;
  resetStream(): void;
  attach(runId: string | null, send: (reply: HandsFreeReply) => Promise<void>): void;
  setTurn(turn: Turn | null): void;
  attachedRun(): string | null;
  /** A page showing the session holds it; when none has for a moment (the user went elsewhere), it ends quietly. */
  hold(): () => void;
}

const Ctx = createContext<HandsFreeApi | null>(null);
const VOICE_KEY = "workspace.voice";
const BARGE_IN_KEY = "workspace.voice.barge_in";

function readFlag(key: string): boolean { try { return localStorage.getItem(key) === "on"; } catch { return false; } }
function writeFlag(key: string, on: boolean) { try { localStorage.setItem(key, on ? "on" : "off"); } catch { /* per page only */ } }

export function HandsFreeProvider({ settings, children, openMic = (onLost) => MicSession.open(onLost) }: {
  settings: HandsFreeSettings; children: ReactNode; openMic?: (onLost: () => void) => Promise<MicLike>;
}) {
  const api = useOptionalScopeApi()?.api;
  const available = settings.available && !!api && typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const phrases = useMemo(() => settings.phrases ?? {}, [settings.phrases]);
  const runId = useRef<string | null>(null);
  const send = useRef<(reply: HandsFreeReply) => Promise<void>>(async () => undefined);
  const synthesize = useCallback((text: string) => (api ? api.synthesize(text, runId.current) : Promise.reject(new Error("no api"))), [api]);
  const player = useSpeechPlayer(synthesize);
  const playerRef = useRef(player);
  playerRef.current = player;
  const clips = useRef(new Map<string, Promise<{ type: string; bytes: Uint8Array }>>());
  const session = useRef<{ hf: HandsFree; mic: MicLike; lock: WakeLockSentinel | null } | null>(null);
  const streamed = useRef("");
  const [on, setOn] = useState(() => readFlag(VOICE_KEY));
  const [state, setState] = useState<HandsFreeState | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [bargeIn, setBargeInState] = useState(() => readFlag(BARGE_IN_KEY));
  const holders = useRef(0);
  const letGo = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clipAudio = useCallback((name: Phrase) => {
    let got = clips.current.get(name);
    if (!got) {
      got = api!.clip(name);
      got.catch(() => clips.current.delete(name));   // not cached when it failed: try again next time
      clips.current.set(name, got);
    }
    // a fresh stream over the cached bytes for every play
    return got.then(({ type, bytes }) => ({ type, body: new ReadableStream<Uint8Array>({ start(c) { c.enqueue(bytes.slice()); c.close(); } }) }));
  }, [api]);

  const close = useCallback(() => {
    const s = session.current;
    session.current = null;
    if (!s) return;
    s.mic.close();
    void s.lock?.release().catch(() => undefined);
    setState(null);
  }, []);

  // one start at a time: StrictMode, or a second tap while the permission prompt is up, would open a second microphone
  const starting = useRef<Promise<boolean> | null>(null);
  const startRef = useRef<() => Promise<boolean>>(async () => false);
  const begin = useCallback(() => (starting.current ??= startRef.current().finally(() => { starting.current = null; })), []);
  startRef.current = async () => {
    if (!api || session.current) return !!session.current;
    warmSpeech(api);   // while the microphone prompt is up, the connection opens
    setProblem(null);
    void playerRef.current.unlock();   // inside the tap: iOS allows sound from here on
    let mic: MicLike;
    try {
      mic = await openMic(() => session.current?.hf.fail());
    } catch (e) {
      const name = e instanceof DOMException ? e.name : "";
      setProblem(name === "NotAllowedError" ? "The microphone is blocked for this site. Allow it in the browser's site settings to use Voice."
        : name === "NotFoundError" ? "No microphone was found." : "This browser can't record audio here.");
      return false;
    }
    const ports: HandsFreePorts = {
      say: async (text) => { if (text.trim()) playerRef.current.speak(text); await playerRef.current.whenIdle(); },
      clip: async (name) => { playerRef.current.play(() => clipAudio(name)); await playerRef.current.whenIdle(); },
      phrase: (name) => phrases[name] ?? "",
      hush: () => playerRef.current.stop(),
      capture: ({ timeoutMs, maxMs }) => mic.capture({ timeoutMs, vad: maxMs ? { maxMs } : undefined }),
      watch: () => mic.watch(),
      transcribe: async (audio, ms) => {
        try {
          return (await api.transcribe(audio, { runId: runId.current, durationMs: ms })).text;
        } catch (e) {
          if (e instanceof ApiError && e.status === 422) throw new NotHeard(e.detail);
          throw e;
        }
      },
      send: (reply) => send.current(reply),
    };
    const hf = new HandsFree(ports, {
      bargeIn: readFlag(BARGE_IN_KEY),
      onState: (s) => { if (s === "ended" || s === "error") close(); else setState(s); },
    });
    session.current = { hf, mic, lock: await wakeLock() };
    setState("idle");
    return true;
  };

  // Keep the screen on, and pause for whatever takes the audio away: the page hidden (a call, the app switcher,
  // the screen locking), a device change (a car kit connecting), an iOS audio interruption.
  const active = state !== null;
  useEffect(() => {
    if (!active) return;
    const s = session.current;
    if (!s) return;
    const onVisible = async () => {
      if (document.visibilityState === "hidden") return s.hf.pause("interrupted");
      s.lock = await wakeLock();
      s.hf.remind();
    };
    const onDevice = () => s.hf.pause("audio");
    const ctx = s.mic.audioContext;
    const onAudio = () => { if ((ctx?.state as string) === "interrupted") s.hf.pause("interrupted"); };
    document.addEventListener("visibilitychange", onVisible);
    navigator.mediaDevices?.addEventListener?.("devicechange", onDevice);
    ctx?.addEventListener("statechange", onAudio);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      navigator.mediaDevices?.removeEventListener?.("devicechange", onDevice);
      ctx?.removeEventListener("statechange", onAudio);
    };
  }, [active]);

  useEffect(() => () => { session.current?.hf.quit(); close(); }, [close]);

  const value: HandsFreeApi = {
    available, on, active, state, phrases, bargeIn, problem, begin,
    toggle: async (next) => {
      writeFlag(VOICE_KEY, next);
      setOn(next);
      if (next) await begin();
      else { session.current?.hf.quit(); setProblem(null); }
    },
    prefer: (next) => { writeFlag(VOICE_KEY, next); setOn(next); },
    stop: () => session.current?.hf.quit(),
    yieldTurn: () => session.current?.hf.yieldTurn(),
    speakChunk: (text) => { streamed.current = `${streamed.current} ${text}`.trim(); playerRef.current.speak(text); },
    streamedText: () => streamed.current,
    resetStream: () => { streamed.current = ""; },
    setBargeIn: (next) => { writeFlag(BARGE_IN_KEY, next); setBargeInState(next); session.current?.hf.setBargeIn(next); },
    attach: useCallback((id: string | null, fn: (reply: HandsFreeReply) => Promise<void>) => { runId.current = id; send.current = fn; }, []),
    setTurn: useCallback((turn: Turn | null) => session.current?.hf.setTurn(turn), []),
    attachedRun: () => runId.current,
    hold: useCallback(() => {
      holders.current += 1;
      if (letGo.current) clearTimeout(letGo.current);
      letGo.current = null;
      let released = false;
      return () => {
        if (released) return;
        released = true;
        holders.current -= 1;
        // a moment's grace: New request hands over to the request's page, and StrictMode remounts
        if (holders.current === 0) letGo.current = setTimeout(() => { if (holders.current === 0) session.current?.hf.quit(); }, 1500);
      };
    }, []),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

async function wakeLock(): Promise<WakeLockSentinel | null> {
  try { return (await navigator.wakeLock?.request("screen")) ?? null; } catch { return null; }
}

const OFF: HandsFreeApi = {
  available: false, on: false, active: false, state: null, phrases: {}, bargeIn: false, problem: null,
  setBargeIn: () => undefined, toggle: async () => undefined, prefer: () => undefined, begin: async () => false, stop: () => undefined, yieldTurn: () => undefined,
  speakChunk: () => undefined, streamedText: () => "", resetStream: () => undefined,
  attach: () => undefined, setTurn: () => undefined, attachedRun: () => null, hold: () => () => undefined,
};

export function useHandsFree(): HandsFreeApi {
  return useContext(Ctx) ?? OFF;
}

/** Keep the session while this page shows it; leaving for anywhere else ends it. */
export function useHandsFreeHold(showing: boolean): void {
  const { hold } = useHandsFree();
  useEffect(() => (showing ? hold() : undefined), [showing, hold]);
}

/**
 * A question streaming in is spoken sentence by sentence while Voice is on (the first sentence as soon as it is
 * 40 characters, later ones about 150). When the question itself lands, its option list is what is left to say.
 */
export function useStreamedSpeech(stream: { text: string; done: boolean } | null): void {
  const hf = useHandsFree();
  const speakRef = useRef(hf.speakChunk);
  speakRef.current = hf.speakChunk;
  const chunker = useRef<SentenceChunker | null>(null);
  chunker.current ??= new SentenceChunker((c) => speakRef.current(c));
  const fed = useRef(0);
  const speaking = hf.active && (hf.state === "thinking" || hf.state === "speaking" || hf.state === "idle");
  useEffect(() => {
    if (!stream || !speaking) return;
    if (stream.text.length < fed.current) { chunker.current!.reset(); fed.current = 0; hf.resetStream(); }   // a new turn
    if (fed.current === 0) hf.resetStream();
    chunker.current!.push(stream.text.slice(fed.current));
    fed.current = stream.text.length;
    if (stream.done) { chunker.current!.flush(); fed.current = 0; }
  }, [stream?.text, stream?.done, speaking]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!stream) { chunker.current!.reset(); fed.current = 0; } }, [!stream]);   // eslint-disable-line react-hooks/exhaustive-deps
}

// ---- what the agent is saying now, from the run ------------------------------------------------------------

export function transcriptOf(run: RunView): IntakeTurn[] {
  if ("intake_transcript" in run && Array.isArray(run.intake_transcript)) return run.intake_transcript;
  const state = (run as { state?: { intake_transcript?: IntakeTurn[] } | null }).state;
  return state?.intake_transcript ?? [];
}

const norm = (s: string) => s.replace(/\s+/g, " ").trim();

/**
 * The turn for the run as it stands: the pending question (as it should be read), a read-back, the review read
 * aloud, or — once the intake is behind it — the closing line. Null while the agent is still working. A question
 * whose words were already spoken as they streamed in has only its option list left to say.
 */
export function handsFreeTurn(run: RunView, phrases: Record<string, string>, streamed = ""): Turn | null {
  if (run.status === "running" || run.status === "idle") return null;   // idle: created, not yet picked up by a worker
  const n = transcriptOf(run).length;
  const next = run.next as { type?: string } | null;
  if (run.status === "waiting" && next?.type === "intake_question") {
    const q = next as IntakeQuestionPayload;
    const text = q.spoken?.prompt ?? q.prompt;
    const turn: Turn = { key: `${n}|${q.question_id}|${q.confirms ?? ""}|${q.prompt}`, kind: q.confirms ? "confirm" : "question", text, prompt: q.prompt, required: q.required };
    if (streamed && norm(streamed) === norm(q.prompt)) {
      const base = q.prompt.trimEnd().replace(/\?$/, "").trimEnd();
      turn.streamed = true;
      turn.remainder = text.startsWith(base) ? text.slice(base.length).replace(/^\s*/, "") : "";
      if (turn.remainder === "?") turn.remainder = "";
    }
    return turn;
  }
  if (run.status === "waiting" && next?.type === "intake_review") {
    const r = next as IntakeReviewPayload;
    return { key: `${n}|review|${r.prompt}`, kind: "review", text: r.spoken_summary ?? r.prompt, review: r.items.map((i) => ({ label: i.label, full: i.value })) };
  }
  if (run.status === "waiting" && next?.type === "requisition_review") {   // CHANGE_requisition_review: read aloud, answered in words
    const r = next as RequisitionReviewPayload;
    return { key: `${n}|requisition|${(r.errors ?? []).join()}|${(r.unplaced ?? []).join()}|${JSON.stringify(r.requisition).length}`, kind: "review",
      text: (r.unplaced?.length ? `I couldn't place ${r.unplaced.join(", ")}. ` : "") + (r.spoken_summary ?? "Here's the requisition. Say continue, or tell me what to change."), review: [] };
  }
  return { key: "end", kind: "end", text: phrases.done ?? "" };
}

const CONTINUE = /^\s*(continue|go on|carry on|yes|yep|ok(ay)?|that'?s (right|fine|good)|looks (good|right|fine)|all good|no changes?)\b[\s.!]*$/i;

/** CHANGE_requisition_review: a reply to the requisition card. The card itself sends an action; anything else is a
 *  spoken or typed reply: going on (or nothing) proceeds, and words that change something are a correction. */
export function requisitionReply(value: ResumeValue): ResumeValue {
  if (value && typeof value === "object" && "action" in value) return value;
  const text = typeof value === "string" ? value : value && typeof value === "object" && "text" in value ? String((value as { text?: unknown }).text ?? "") : "";
  if (!text.trim() || CONTINUE.test(text) || (value && typeof value === "object" && "confirm" in value)) return { action: "proceed", via: "voice" } as ResumeValue;
  return { action: "say", text, via: "voice" } as ResumeValue;
}

/**
 * The reply as sent: with `hands_free` added only when the Voice toggle differs from what the run last heard, on the
 * reply shapes that can carry it (a question's answer, the review, go back). Someone who never touches the toggle
 * sends exactly what they always did.
 */
export function withToggle(value: ResumeValue, toggle: boolean, run: boolean, pendingType: string | undefined): ResumeValue {
  if (toggle === run || (pendingType !== "intake_question" && pendingType !== "intake_review")) return value;
  if (typeof value === "string") return (pendingType === "intake_review" && !value.trim() ? { confirm: true, hands_free: toggle } : { text: value, hands_free: toggle }) as ResumeValue;
  if (value && typeof value === "object") return { ...value, hands_free: toggle } as ResumeValue;
  return value;
}

/** Replies from a run page: the hands-free reply is a resume value as it stands. */
export function asResume(reply: HandsFreeReply): ResumeValue {
  return reply as ResumeValue;
}

// ---- the control: Voice, its state, Stop, and barge-in -------------------------------------------------------

const STATE_LABEL: Partial<Record<HandsFreeState, string>> = { idle: "Starting…", speaking: "Speaking", listening: "Listening", thinking: "Thinking", typing: "Your turn to type", paused: "Paused" };

/**
 * Beside the chat: the Voice toggle, what it is doing, Stop, and the barge-in switch. On but not running (a page
 * load; the microphone never opens by itself) it offers "Tap to resume" — or "Tap to begin" from the shortcut.
 */
export function VoiceControls({ disabled, disabledHint, tapLabel = "Tap to resume", tapRef, description }: {
  disabled?: boolean; disabledHint?: string; tapLabel?: string; tapRef?: RefObject<HTMLButtonElement | null>; description?: string;
}) {
  const hf = useHandsFree();
  if (!hf.available) return null;
  const waiting = hf.on && !hf.active;
  return (
    <div className={`voice-controls${hf.active ? " live" : ""}`}>
      <div className="voice-bar">
        <button type="button" className={`voice-toggle${hf.on ? " on" : ""}`} aria-pressed={hf.on} disabled={disabled && !hf.on} onClick={() => void hf.toggle(!hf.on)}>
          <SpeakerIcon /> Voice
        </button>
        {waiting && <button ref={tapRef} type="button" className={tapLabel === "Tap to begin" ? "hf-start-btn" : "pill small"} disabled={disabled} onClick={() => void hf.begin()}>{tapLabel}</button>}
        <span role="status" aria-live="polite" className={`hf-state-inline hf-${hf.state ?? "off"}`}>
          {hf.active && <><span className="hf-dot" aria-hidden />{STATE_LABEL[hf.state ?? "idle"]}</>}
        </span>
        {hf.active && <button type="button" className="link small" onClick={hf.stop}>Stop</button>}
        {hf.on && <label className="hf-switch small"><input type="checkbox" checked={hf.bargeIn} onChange={(e) => hf.setBargeIn(e.target.checked)} /> Interrupt by speaking — works best with a headset</label>}
      </div>
      {description && <p className="muted small voice-description">{description}</p>}
      {disabled && disabledHint && <p className="muted small">{disabledHint}</p>}
      {hf.problem && <p role="alert" className="mic-problem-inline">{hf.problem}</p>}
    </div>
  );
}

function SpeakerIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M19 5a10 10 0 0 1 0 14" /></svg>;
}
